import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, UtensilsCrossed, HandHeart, Sparkles, Quote } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import givingHero from "@/assets/giving-food-hero.jpg";

import armandsOrphanage from "@/assets/armands-orphanage-latvia.jpg";
import newlifeLogo from "@/assets/newlife-pomona-logo.webp";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "SimpleTech",
  telephone: "(909) 255-9112",
  email: "contact@simpletechhelper.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "8880 N Benson Ave #121",
    addressLocality: "Montclair",
    addressRegion: "CA",
    postalCode: "91763",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "AdministrativeArea", name: "Montclair" },
    { "@type": "AdministrativeArea", name: "Pomona" },
    { "@type": "AdministrativeArea", name: "Inland Empire" },
    { "@type": "AdministrativeArea", name: "Ontario" },
    { "@type": "AdministrativeArea", name: "Rancho Cucamonga" },
  ],
  knowsAbout: [
    "Managed IT Services",
    "Cybersecurity",
    "Small Business Website Design",
    "ADA Website Compliance",
    "VoIP",
    "Software Integration",
    "Tech Support for Seniors",
  ],
};

export const Route = createFileRoute("/giving-back")({
  component: GivingBackPage,
  head: () => ({
    meta: [
      { title: "Technology With Purpose | SimpleTech Gives Back Pomona" },
      {
        name: "description",
        content:
          "When you choose SimpleTech for IT, cybersecurity, or web design, a portion of your invoice automatically feeds a family at Newlife Pomona Food Bank.",
      },
      { property: "og:title", content: "Technology With Purpose | SimpleTech Gives Back Pomona" },
      {
        property: "og:description",
        content:
          "A portion of every IT, cybersecurity, and web design project at SimpleTech goes to Newlife Pomona Food Bank — feeding families across the Inland Empire.",
      },
      seoOgUrl("/giving-back"),
      { property: "og:type", content: "website" },
      { property: "og:image", content: givingHero },
    ],
    links: [seoCanonicalLink("/giving-back")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(LOCAL_BUSINESS_SCHEMA),
      },
    ],
  }),
});

const SERVICE_LINKS: Array<{ label: string; slug: string }> = [
  { label: "Managed IT Services", slug: "managed-it-services" },
  { label: "Cyber Security", slug: "cyber-security" },
  { label: "Small Business Website Design", slug: "website-design" },
  { label: "ADA Website Compliance", slug: "ada-compliant-website" },
  { label: "VoIP Solutions", slug: "voip" },
  { label: "Software Integration", slug: "software-integration" },
  { label: "Tech Support for Seniors", slug: "tech-support-for-seniors" },
];

function GivingBackPage() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="giving" />
      <main id="main-content" className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden text-white min-h-[640px] md:min-h-[760px] flex items-center">
          <div
            className="absolute inset-0 bg-cover bg-center animate-drift"
            style={{ backgroundImage: `url(${givingHero})` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/85" aria-hidden />


          <div className="relative z-10 max-w-6xl mx-auto px-6 py-24 md:py-32 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-sm font-semibold tracking-wider uppercase animate-fade-in">
              <span>Giving Back Through Every Project</span>
            </div>
            <h1 className="mt-6 text-4xl md:text-7xl font-extrabold tracking-tight leading-[1.05] animate-fade-in">
              When you work with SimpleTech,
              <br />
              someone <span className="italic font-black">eats tonight.</span>
            </h1>
            <p className="mt-8 max-w-3xl mx-auto text-lg md:text-xl text-white/90 leading-relaxed animate-fade-in">
              At SimpleTech, technology is only part of what we do. A portion of every IT,
              cybersecurity, and web design project we complete in the Inland Empire helps
              provide meals for local families through our partnership with{" "}
              <span className="font-semibold text-white">Newlife Pomona Food Bank</span>.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 animate-fade-in">
              <a
                href="https://www.newlifepomona.com/foodbank"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#27c05f] hover:bg-[#1fa34f] text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <HandHeart className="w-4 h-4" /> VISIT NEWLIFE FOOD BANK
              </a>
              <a
                href="https://pushpay.com/g/newlifepomona?r=monthly&src=hpp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block border-2 border-white/70 hover:bg-white hover:text-[#0b0a3d] text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                Donate
              </a>

            </div>
          </div>
        </section>

        {/* SERVICE LINKS — internal SEO juice */}
        <section className="bg-white py-16 md:py-20 border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <p className="text-sm font-bold tracking-[0.3em] text-[#1a8a44] uppercase">Your Project Feeds Someone</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0b0a3d] tracking-tight">
              Every service we offer gives back.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Whether you hire SimpleTech for any of the services below, a portion of your
              invoice goes straight to feeding Inland Empire families.
            </p>
            <ul className="mt-10 flex flex-wrap items-center justify-center gap-3">
              {SERVICE_LINKS.map(({ label, slug }) => (
                <li key={slug}>
                  <Link
                    to="/services/$slug"
                    params={{ slug }}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-200 bg-slate-50 hover:bg-[#27c05f]/10 hover:border-[#27c05f]/30 hover:text-[#136833] text-[#0b0a3d] font-semibold text-sm transition-colors"
                  >
                    <Heart className="w-4 h-4 text-[#1a8a44]" /> {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* HOW IT WORKS — counters */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-sm font-bold tracking-[0.3em] text-[#1a8a44] uppercase">How It Works</p>
              <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
                Simple, automatic, and 100% local.
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                {
                  stat: "Every",
                  label: "Project Gives Back",
                  body: "A portion of every invoice is donated to Newlife Pomona Food Bank — automatically, with every completed project.",
                },
                {
                  stat: "100%",
                  label: "Local Impact",
                  body: "Every dollar stays in the Inland Empire — helping families in Pomona, Montclair, Ontario, and surrounding communities.",
                },
                {
                  stat: "1 Meal",
                  label: "At a Time",
                  body: "We may not solve world hunger overnight. But together, we can help make sure someone eats tonight.",
                },
              ].map(({ stat, label, body }, i) => (
                <div
                  key={label}
                  className="rounded-2xl border border-slate-100 bg-gradient-to-b from-white to-slate-50 p-10 shadow-sm hover:shadow-xl transition-shadow animate-count-up"
                  style={{ animationDelay: `${i * 120}ms` }}
                >
                  <p className="text-5xl md:text-6xl font-extrabold text-[#1a8a44]">{stat}</p>
                  <p className="mt-2 text-base font-bold tracking-widest text-[#0b0a3d] uppercase">{label}</p>
                  <p className="mt-6 text-slate-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* THE STORY — Armands' why */}
        <section className="relative bg-[#0b0a3d] text-white py-20 md:py-28 overflow-hidden">
          <div className="absolute -top-10 -left-10 text-[20rem] text-white/[0.04] font-extrabold leading-none select-none" aria-hidden>
            WHY
          </div>
          <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={armandsOrphanage}
                alt="SimpleTech founder Armands as a child in the orphanage where he grew up in Latvia — the story behind our mission to feed Pomona families"
                loading="lazy"
                width={1280}
                height={853}
                className="rounded-2xl shadow-2xl w-full h-auto object-cover animate-fade-in"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#27c05f] text-white px-6 py-4 rounded-xl shadow-xl animate-float-slow">
                <Quote className="w-6 h-6 mb-1" />
                <p className="text-sm font-bold tracking-wide">A table was set.</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-bold tracking-[0.3em] text-sky-400 uppercase">The Story Behind SimpleTech</p>
              <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                The owner of SimpleTech grew up in an orphanage in Latvia.
              </h2>
              <div className="mt-8 space-y-5 text-lg text-white/85 leading-relaxed">
                <p>
                  Food was donated, but hunger was still part of everyday life. Meals came
                  only at scheduled times — oatmeal or cereal for breakfast, soup for lunch,
                  soup again for dinner.
                </p>
                <p>
                  Fresh fruit and vegetables were rare. In fact, he once thought lettuce was
                  animal food because he had never seen it before.
                </p>
                <p className="text-2xl font-bold text-white">
                  That experience never left him.
                </p>
                <p>
                  Today, SimpleTech exists not only to provide reliable IT support, cybersecurity,
                  and web design services across the Inland Empire — but also to help families
                  who are struggling to put food on the table.
                </p>
                <p className="italic text-sky-300">
                  When you choose SimpleTech, your business feeds someone else.
                  Thank you for being part of that mission.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* PARTNER — Newlife */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-sm font-bold tracking-[0.3em] text-[#1a8a44] uppercase">Our Community Partner</p>
              <a href="https://www.newlifepomona.com/foodbank" target="_blank" rel="noopener noreferrer" className="mt-6 inline-block">
                <img
                  src={newlifeLogo}
                  alt="Newlife Pomona Food Bank official logo"
                  loading="lazy"
                  className="h-24 md:h-32 w-auto mx-auto"
                />
              </a>
              <h2 className="mt-6 text-4xl md:text-6xl font-extrabold text-[#0b0a3d] tracking-tight">
                Newlife Pomona Food Bank
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Newlife Pomona Food Bank serves hundreds of families throughout Pomona and
                the Inland Empire every month with groceries, fresh produce, pantry staples,
                and compassion. Every project completed by SimpleTech helps support that mission.
              </p>
            </div>

            <div className="mt-14 grid md:grid-cols-2 gap-8 items-center">
              <div className="relative overflow-hidden rounded-2xl shadow-xl bg-black">
                <div className="aspect-video w-full">
                  <iframe
                    src="https://www.youtube.com/embed/FchZytN23E4"
                    title="Newlife Pomona Food Bank"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
              <div className="space-y-6">
                {[
                  {
                    Icon: UtensilsCrossed,
                    title: "Fresh groceries every week",
                    body: "Families receive produce, bread, proteins, and essential pantry items distributed directly to local neighbors in need.",
                  },
                  {
                    Icon: Heart,
                    title: "Dignity, not pity",
                    body: "Every person who walks through Newlife's doors is treated with kindness, respect, and dignity.",
                  },
                  {
                    Icon: Sparkles,
                    title: "Faith in action",
                    body: "More than a food bank — Newlife is a community committed to serving people with love and compassion.",
                  },
                ].map(({ Icon, title, body }, i) => (
                  <div
                    key={title}
                    className="flex gap-5 p-5 rounded-xl border border-slate-100 hover:border-[#27c05f]/30 hover:shadow-md transition-all animate-count-up"
                    style={{ animationDelay: `${i * 120}ms` }}
                  >
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-[#27c05f]/10 text-[#1a8a44] flex items-center justify-center">
                      <Icon className="w-6 h-6" strokeWidth={1.8} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#0b0a3d]">{title}</h3>
                      <p className="mt-1 text-slate-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                ))}
                <a
                  href="https://pushpay.com/g/newlifepomona?r=monthly&src=hpp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#27c05f] hover:bg-[#1fa34f] text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors mt-2"
                >
                  <HandHeart className="w-4 h-4" /> DONATE TO NEWLIFE
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="relative bg-[#0b0a3d] text-white py-20 md:py-28 overflow-hidden">
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <p className="text-sm font-bold tracking-[0.3em] text-sky-400 uppercase">Technology With Purpose</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
              Your project. Someone's dinner tonight.
            </h2>
            <p className="mt-5 text-white/85 text-lg leading-relaxed">
              Hire SimpleTech for{" "}
              <Link to="/services/$slug" params={{ slug: "managed-it-services" }} className="underline decoration-sky-400 hover:text-sky-300">Managed IT</Link>,{" "}
              <Link to="/services/$slug" params={{ slug: "cyber-security" }} className="underline decoration-sky-400 hover:text-sky-300">Cybersecurity</Link>,{" "}
              <Link to="/services/$slug" params={{ slug: "website-design" }} className="underline decoration-sky-400 hover:text-sky-300">Web Design</Link>,{" "}
              <Link to="/services/$slug" params={{ slug: "ada-compliant-website" }} className="underline decoration-sky-400 hover:text-sky-300">ADA Compliance</Link>,{" "}
              <Link to="/services/$slug" params={{ slug: "voip" }} className="underline decoration-sky-400 hover:text-sky-300">VoIP</Link>, or a{" "}
              <Link to="/services/$slug" params={{ slug: "tech-support-for-seniors" }} className="underline decoration-sky-400 hover:text-sky-300">senior tech visit</Link>{" "}
              — and a portion of what you pay shows up at an Inland Empire family's table this month.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://pushpay.com/g/newlifepomona?r=monthly&src=hpp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#27c05f] hover:bg-[#1fa34f] text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                Donate
              </a>

              <Link
                to="/services"
                className="inline-block border-2 border-sky-400 text-white hover:bg-sky-400 font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                SEE OUR SERVICES
              </Link>
            </div>
            
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
